import {
  TiendaPerfectaDeliveredMaterial,
  TiendaPerfectaFormStep,
  TiendaPerfectaStopDetail,
  TiendaPerfectaStopMaterials,
  TiendaPerfectaSuggestedMaterial,
} from 'models/TiendaPerfecta';

import { StopUiState } from '../types';

/**
 * Toda la lógica del detalle de una parada que no es pintar: el estado de la
 * parada, el de sus dos actividades, y el cruce de materiales.
 *
 * Vive fuera de los componentes porque son las reglas que hay que tocar si el
 * contrato cambia, y porque son lo único de esta pantalla que se puede probar
 * sin montar un modal.
 */

/**
 * Los cuatro estados de UI de una parada, ahora leídos del DETALLE.
 *
 * El detalle no trae `bypass` en la parada: la excepción vive en `incident`, y
 * lo que la marca es `usedForBypass` —lo que PASÓ—, no `allowsBypass`, que sólo
 * dice qué permitiría el catálogo. Es `usedForBypass` el que se corresponde con
 * el `bypass` no nulo que el reporte publica para la misma parada, así que la
 * grilla y el modal coinciden.
 */
export const getStopDetailUiState = (
  detail: TiendaPerfectaStopDetail
): StopUiState => {
  if (detail.stop.status === 'finished') {
    return detail.incident?.usedForBypass ? 'not_executed' : 'executed';
  }

  if (detail.stop.status === 'in_progress') return 'on_going';

  return 'pending';
};

/**
 * Cuántos pasos tienen respuesta. Un `answer` nulo es "espera respuesta".
 *
 * El paso de tipo `materials` es la excepción: su contenido no está en el
 * `answer` sino en el bloque `materials`, así que lo que decide si está
 * respondido es si se dejó material, no si trae `answer`. Sin esta excepción
 * una parada ejecutada al 100 % nunca podría llegar a "Completada" en cuanto su
 * plantilla incluyera un paso de materiales.
 */
export const countAnsweredSteps = (
  steps: TiendaPerfectaFormStep[],
  materials?: TiendaPerfectaStopMaterials
): number =>
  steps.filter((step) =>
    step.type === 'materials'
      ? hasDeliveredMaterials(materials)
      : step.answer !== null
  ).length;

/* ─────────────────────────── Actividades ───────────────────────────
   Son DOS y son fijas, porque el backend publica dos bloques distintos: el
   formulario POP y la incidencia. No es una lista que crezca sola.

   `StopActivityKey` vive en `StopActivityModal`, que es quien la consume: acá
   sólo están las reglas de si cada actividad se realizó.                      */

/**
 * Si la actividad puede abrirse.
 *
 * Hace falta la plantilla y alguna evidencia. Tener sólo la plantilla no
 * alcanza: una parada pendiente publica sus pasos completos con todos los
 * `answer` en `null`, y ahí no hay nada que mostrar salvo el formulario en
 * blanco.
 *
 * El material ENTREGADO cuenta como evidencia por sí solo, tenga o no la
 * plantilla un paso que lo declare: el paso de tipo `materials` no guarda su
 * contenido en el `answer`, y lo entregado se publica igual. El material
 * SUGERIDO no cuenta: una parada que nadie visitó publica igual sus
 * sugerencias, así que aceptarlas daría por realizada una actividad que no
 * ocurrió.
 *
 * El `steps.length > 0` no es redundante: la segunda condición mira los
 * materiales y no los pasos, así que sin él una parada sin plantilla pero con
 * material entregado abriría un modal de actividad vacío.
 */
export const isPopActivityDone = (detail: TiendaPerfectaStopDetail): boolean =>
  detail.form.steps.length > 0 &&
  (countAnsweredSteps(detail.form.steps, detail.materials) > 0 ||
    hasDeliveredMaterials(detail.materials));

/** Si la plantilla declara dónde van los materiales dentro del formulario. */
export const hasMaterialsStep = (steps: TiendaPerfectaFormStep[]): boolean =>
  steps.some((step) => step.type === 'materials');

/**
 * Si se dejó material en el local.
 *
 * Es lo único que prueba que la parada se atendió: lo sugerido viaja en el
 * payload de una parada pendiente, así que no prueba nada.
 */
export const hasDeliveredMaterials = (
  materials?: TiendaPerfectaStopMaterials
): boolean => Boolean(materials && materials.delivered.length > 0);

export const isIncidentActivityDone = (
  detail: TiendaPerfectaStopDetail
): boolean => detail.incident !== null;

/**
 * Si la parada se cerró POR EXCEPCIÓN y hay que anunciar el motivo arriba.
 *
 * Una incidencia registrada que no cerró la parada no explica el resultado, así
 * que no levanta la alerta: vive en su card de actividad y con eso alcanza.
 */
export const usedIncidentForBypass = (
  detail: TiendaPerfectaStopDetail
): boolean => Boolean(detail.incident?.usedForBypass);

/* ─────────────────────────── Materiales ───────────────────────────
   Dos arreglos que la API entrega SIN cruzar. Acá se juntan para poder leerlos
   uno al lado del otro, agrupados por paquete.                                */

/**
 * Una línea de la tabla: el material y sus dos cantidades.
 *
 * No lleva `labelSource` ni una marca de "no estaba sugerido". El primero dice
 * de qué catálogo salió el NOMBRE del material —una distinción del backend que
 * en pantalla sonaba a jerga—, y lo segundo ya lo comunica la propia columna,
 * que muestra una raya cuando no hubo sugerencia.
 */
export interface StopMaterialRow {
  materialId: string;
  materialSku: string;
  materialName: string;
  suggested: number;
  delivered: number;
}

export interface StopMaterialGroup {
  key: string;
  sourceType: string;
  packageName: string | null;
  packageSku: string | null;
  packageQuantity: number | null;
  rows: StopMaterialRow[];
}

/**
 * La clave con la que se cruza una línea sugerida con una entregada.
 *
 * NO es `materialId`: el mismo material puede venir dentro de un paquete Y
 * suelto en la misma parada, con cantidades distintas, y el backend los publica
 * como dos líneas a propósito. Cruzar sólo por material las fusionaría en una
 * fila que no existe en ningún lado.
 */
const lineKey = (line: {
  materialId: string;
  sourceType: string;
  packageId: string | null;
}): string => `${line.materialId}|${line.sourceType}|${line.packageId ?? ''}`;

const LOOSE_GROUP_KEY = '__loose__';

/**
 * Junta sugerido y entregado en un solo árbol: paquetes primero, con las líneas
 * de un mismo paquete juntas, y los sueltos al final. Es el mismo orden que ya
 * garantiza el contrato para los dos arreglos, así que no se reordena nada.
 *
 * No calcula diferencias a propósito. Lo sugerido NO es una meta: en terreno se
 * puede dejar más de lo sugerido, menos, o material que nadie sugirió, y una
 * columna "faltante" convertiría una diferencia normal en un incumplimiento.
 */
export const groupStopMaterials = (
  materials: TiendaPerfectaStopMaterials
): StopMaterialGroup[] => {
  /**
   * Los dos arreglos se acumulan POR CLAVE antes de emitir ninguna fila.
   *
   * Acumular primero es lo que hace idempotente el cruce. Emitir una fila por
   * línea rompe de dos maneras si una clave se repite: dos líneas sugeridas
   * iguales mostrarían cada una el total entregado —contándolo dos veces—, y
   * dos entregadas sin sugerir mostrarían sólo la primera. En ambos casos la
   * tabla terminaría contradiciendo el total que `getStopMaterialTotals`
   * calcula sobre los arreglos completos, en la misma pantalla.
   */
  const suggestedByKey = new Map<string, number>();
  const deliveredByKey = new Map<string, number>();

  materials.suggested.forEach((line) => {
    const key = lineKey(line);
    suggestedByKey.set(
      key,
      (suggestedByKey.get(key) ?? 0) + (line.suggestedQuantity ?? 0)
    );
  });

  materials.delivered.forEach((line) => {
    const key = lineKey(line);
    /* El `?? 0` es el mismo que usa `getStopMaterialTotals`: sin él, una
       cantidad ausente daría `NaN` en la fila mientras el pie sigue mostrando
       un número — justo la contradicción que este cruce existe para evitar. */
    deliveredByKey.set(
      key,
      (deliveredByKey.get(key) ?? 0) + (line.quantity ?? 0)
    );
  });

  const groups = new Map<string, StopMaterialGroup>();
  const emitted = new Set<string>();

  const pushRow = (
    line: TiendaPerfectaSuggestedMaterial | TiendaPerfectaDeliveredMaterial
  ) => {
    const key = lineKey(line);
    if (emitted.has(key)) return;
    emitted.add(key);

    /* Un `sourceType: 'package'` sin `packageId` no se puede atribuir a ningún
       paquete, así que cae entre los sueltos. Lo que NO puede hacer es estampar
       ese grupo como paquete: si fuera la primera línea del grupo, todos los
       materiales realmente sueltos que vinieran después quedarían bajo un
       encabezado de paquete que no es el suyo. Por eso la identidad del grupo
       sale de su CLAVE y no de la línea que lo creó. */
    const inPackage = line.sourceType === 'package' && Boolean(line.packageId);
    const groupKey = inPackage ? (line.packageId as string) : LOOSE_GROUP_KEY;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        key: groupKey,
        sourceType: inPackage ? 'package' : 'loose',
        packageName: inPackage ? line.packageName : null,
        packageSku: inPackage ? line.packageSku : null,
        packageQuantity: inPackage
          ? (line as TiendaPerfectaSuggestedMaterial).packageQuantity ?? null
          : null,
        rows: [],
      });
    }

    groups.get(groupKey)?.rows.push({
      materialId: line.materialId,
      materialSku: line.materialSku,
      materialName: line.materialName,
      suggested: suggestedByKey.get(key) ?? 0,
      delivered: deliveredByKey.get(key) ?? 0,
    });
  };

  /* Lo sugerido marca el orden, que es el que ya garantiza el contrato. */
  materials.suggested.forEach(pushRow);

  /**
   * Una línea entregada que nadie sugirió también tiene que aparecer: la API no
   * cruza los arreglos, así que `delivered` no es un subconjunto de
   * `suggested`. Dejar material no sugerido es una operación válida, no un
   * error de datos.
   */
  materials.delivered.forEach(pushRow);

  return Array.from(groups.values()).sort((a, b) => {
    if (a.sourceType === b.sourceType) return 0;

    return a.sourceType === 'package' ? -1 : 1;
  });
};

export interface StopMaterialTotals {
  suggested: number;
  delivered: number;
}

/**
 * Los dos totales, por separado y sin restarse. `delivered` puede superar a
 * `suggested` sin que eso sea una anomalía.
 */
export const getStopMaterialTotals = (
  materials: TiendaPerfectaStopMaterials
): StopMaterialTotals => ({
  suggested: materials.suggested.reduce(
    (total, line) => total + (line.suggestedQuantity ?? 0),
    0
  ),
  delivered: materials.delivered.reduce(
    (total, line) => total + (line.quantity ?? 0),
    0
  ),
});

export const hasStopMaterials = (
  materials: TiendaPerfectaStopMaterials
): boolean => materials.suggested.length > 0 || materials.delivered.length > 0;

/* ────────────────────────────── Mapa ──────────────────────────────
   Los tres puntos de interés de una parada, en el orden en que se leen.       */

export type StopMapPointKey = 'planned' | 'start' | 'finish';

export const STOP_MAP_POINTS: StopMapPointKey[] = [
  'planned',
  'start',
  'finish',
];

/**
 * Colores de los tres puntos. Son los mismos de `EvidenceMap` en el módulo de
 * KIT, para no tener dos códigos de color distintos para la misma idea dentro
 * del producto. Van fijos: son semánticos, así que no pueden salir de
 * `colorPrimary`, que cambia por compañía.
 */
export const STOP_MAP_COLORS: Record<StopMapPointKey, string> = {
  planned: '#8E2DD2',
  start: '#2ECC71',
  finish: '#FF4A4A',
};

/**
 * Si el inicio y el fin cayeron en la misma coordenada.
 *
 * Dos marcadores superpuestos son indistinguibles y sólo se puede tocar el de
 * arriba, así que en ese caso se dibuja uno solo. La leyenda sigue teniendo sus
 * dos entradas, que es lo que permite saber que son dos lecturas y no una.
 *
 * Se comparan como STRING, que es como llegan: parsearlos para esto sólo
 * agregaría una conversión que puede perder precisión.
 */
export const isSameStartAndFinish = (
  detail: TiendaPerfectaStopDetail
): boolean => {
  const { start, finish } = detail.stop.coordinates;

  return Boolean(
    start && finish && start.lat === finish.lat && start.lng === finish.lng
  );
};
